import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ConnectionTest from '../ConnectionTest/ConnectionTest';
import './Layout.css';

// Role-based navigation mapping
const ROLE_NAV_MAP = {
  demand_planner: ['dashboard', 'simulation', 'promotional', 'adhoc', 'aggregation', 'customer_orders'],
  brand_manager: ['dashboard', 'promotional', 'aggregation'],
  sales_manager: ['dashboard', 'promotional', 'aggregation'],
  sc_analyst: ['dashboard', 'simulation', 'aggregation'],
  executive: ['dashboard', 'promotional', 'aggregation', 'customer_orders'],
  admin: ['dashboard', 'simulation', 'promotional', 'adhoc', 'aggregation', 'customer_orders']
};

const NAV_ITEMS = [
  { id: 'dashboard',    to: '/dashboard',    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>, label: 'Dashboard' },
  { id: 'simulation',   to: '/simulation',   icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v7.31"/><path d="M14 9.3V1.99"/><path d="M8.5 2h7"/><path d="M14 9.3a6.5 6.5 0 1 1-4 0"/><path d="M5.52 16h12.96"/></svg>, label: 'Simulation' },
  { id: 'promotional',  to: '/promotional',  icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>, label: 'Promotional Analysis' },
  { id: 'adhoc',        to: '/adhoc',        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>, label: 'Ad-Hoc Forecast' },
  { id: 'aggregation',  to: '/aggregation',  icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M22 20H2"/></svg>, label: 'Aggregation' },
  { id: 'customer_orders', to: '/customer-orders', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12h6"/><path d="M9 16h6"/><path d="M9 8h6"/><path d="M6 4h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/></svg>, label: 'Customer Orders' }
];

const Layout = ({
  activeWindow,
  setActiveWindow,
  connectionStatus,
  setConnectionStatus,
  setError,
  children
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Determine allowed navigation items based on role
  const role = user?.role || 'demand_planner';
  
  // Ad-hoc tab visibility based on role
  const showAdHoc = ['demand_planner', 'sc_analyst', 'admin'].includes(role);
  const showSimulation = role !== 'executive';
  
  const filteredNavItems = NAV_ITEMS.filter(item => {
    if (item.id === 'adhoc') return showAdHoc;
    if (item.id === 'simulation') return showSimulation && ROLE_NAV_MAP[role]?.includes('simulation');
    return true; // Show all other tabs for everyone
  });

  const handleSignOut = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  return (
    <div className={`app-layout ${sidebarOpen ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      {/* Side Navigation */}
      <nav className="side-nav" aria-label="Main navigation" role="navigation">
        <button className="sidebar-toggle floating-toggle" onClick={toggleSidebar} aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'} aria-expanded={sidebarOpen}>
          <svg className={`toggle-icon-svg ${!sidebarOpen ? 'rotated' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <ul className="nav-list">
          {filteredNavItems.map(item => (
            <li key={item.id}>
              <button className={`nav-item ${activeWindow === item.id ? 'active' : ''}`} onClick={() => setActiveWindow(item.id)} aria-current={activeWindow === item.id ? 'page' : undefined} aria-label={item.label} title={!sidebarOpen ? item.label : undefined}>
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </button>
            </li>
          ))}
        </ul>
        <div className="nav-footer">
          <ConnectionTest connectionStatus={connectionStatus} setConnectionStatus={setConnectionStatus} setError={setError} compact={true} />
          <button className="signout-btn" onClick={handleSignOut} aria-label="Sign out of application">
            <svg className="signout-icon" aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            {sidebarOpen && <span className="signout-text">Sign Out</span>}
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="main-content" id="main-content" tabIndex={-1} aria-label="Main content">
        {children}
      </main>
    </div>
  );
};

export default Layout;
